import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const OBSERVATIONS = { works: 'Works', no_effect: 'No effect', uncertain: 'Uncertain' };
const operationLabel = (entry) => {
  const params = entry?.operation?.parameters || {};
  const detail = ['id', 'volume', 'mute', 'output', 'digits', 'count', 'button']
    .filter((key) => params[key] != null && typeof params[key] !== 'object')
    .map((key) => `${key}: ${params[key]}`).join(', ');
  return `${actionLabel(entry?.action)}${entry?.operation?.variant ? ` · ${entry.operation.variant}` : ''}${detail ? ` · ${detail}` : ''}`;
};
const actionLabel = (action) => String(action || '').replace(/^KEYCODE_/, '').replace(/_/g, ' ');

function FieldResultEditor({ deviceId, method, attempts = [] }) {
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [observation, setObservation] = useState('');
  const [note, setNote] = useState('');
  const [context, setContext] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const alive = useRef(true);
  const writing = useRef(false);
  const generation = useRef(0);
  const eligible = attempts.filter((entry) => entry.fieldReceipt && entry.transport === method
    && Number(entry.target?.deviceId) === Number(deviceId));
  const options = selected && !eligible.some((entry) => entry.fieldReceipt === selected.fieldReceipt)
    ? [selected, ...eligible] : eligible;
  const url = `/api/site-control/targets/${deviceId}/field-results`;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(async () => {
    const request = ++generation.current;
    const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, params: { method } });
    if (alive.current && request === generation.current) setResults(response.data.results || []);
  }, [url, method, token]);

  useEffect(() => {
    alive.current = true;
    load().catch(() => { if (alive.current) setError('Could not load field results. Reopen this section to try again.'); });
    return () => { alive.current = false; generation.current++; };
  }, [load]);

  const save = async () => {
    if (!selected || !observation || writing.current) return;
    writing.current = true;
    setSaving(true); setError(''); setMessage('');
    try {
      await axios.post(url, { receipt: selected.fieldReceipt, observation, note, context }, { headers });
      if (!alive.current) return;
      setMessage('Field result saved.');
      try { await load(); } catch { if (alive.current) setError('Saved, but the result list could not refresh. Reopen this section to reload it.'); }
    } catch (failure) {
      if (alive.current) setError(failure.response?.data?.message || 'Could not save the result. Your observation is still here.');
    } finally { writing.current = false; if (alive.current) setSaving(false); }
  };

  return <div className="remote-field-results-body">
    {method === 'lg_webos_remote' && <p>Receipts expire after 24 hours. Record context: Home, FPT, Live TV, HDMI/CEC or Other; include actual effect and any other TV that reacted.</p>}
    <p className="dashboard-tv-remote-hint">Record what you see on this device. These observations do not change its controls.</p>
    <fieldset disabled={saving}>
      <label>Command tested
        <select value={selected?.fieldReceipt || ''} onChange={(event) => {
          setSelected(options.find((entry) => entry.fieldReceipt === event.target.value) || null);
          setObservation(''); setNote(''); setMessage(''); setError('');
        }}>
          <option value="">Choose a recent command</option>
          {options.map((entry) => <option key={entry.fieldReceipt} value={entry.fieldReceipt}>
            {operationLabel(entry)} · {entry.completedAt ? new Date(entry.completedAt).toLocaleTimeString() : entry.id}
          </option>)}
        </select>
      </label>
      {!eligible.length && <p className="dashboard-tv-remote-hint">Send a command using this method, then select it here.</p>}
      {selected && <p className="dashboard-tv-remote-hint">Delivery: {selected.status === 'dispatched' ? 'dispatched; visible effect unverified' : selected.status}.</p>}
      <label>What happened?
        <select value={observation} onChange={(event) => setObservation(event.target.value)}>
          <option value="">Choose an observation</option>
          {Object.entries(OBSERVATIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label>Note (optional)<textarea maxLength={500} rows={2} value={note} onChange={(event) => setNote(event.target.value)} /></label>
      <label>Model, firmware / relay version, test setup (optional)
        <input maxLength={300} value={context} onChange={(event) => setContext(event.target.value)} />
      </label>
      <button type="button" className="secondary" disabled={!selected || !observation || saving} onClick={save}>{saving ? 'Saving…' : 'Save field result'}</button>
    </fieldset>
    {message && <p role="status">{message}</p>}
    {error && <p role="alert">{error}</p>}
    <h5>Saved observations</h5>
    {!results.length && <p className="dashboard-tv-remote-hint">No saved observations for this method.</p>}
    <ul>{results.map((entry) => <li key={entry.id}>
      <details><summary>{operationLabel(entry.evidence)} · {OBSERVATIONS[entry.observation]}{entry.setupChanged ? ' · Setup changed — retest' : ''}</summary>
        <p>{new Date(entry.observedAt).toLocaleString()} · Operator observation</p>
        <p>Delivery: {entry.evidence.status}{entry.evidence.code ? ` (${entry.evidence.code})` : ''} · {entry.evidence.repeat} press(es)</p>
        <p>Request: {entry.evidence.requestId}{entry.evidence.relayDeviceId ? ` · Relay ${entry.evidence.relayDeviceId}` : ' · Relay unknown'}</p>
        {entry.note && <p>{entry.note}</p>}{entry.context && <p>{entry.context}</p>}
      </details>
    </li>)}</ul>
  </div>;
}

export default function RemoteFieldResults(props) {
  const [open, setOpen] = useState(false);
  return <details className="remote-field-results" onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary>Field results</summary>
    {open && <FieldResultEditor {...props} />}
  </details>;
}
